import {Button, Dialog, DialogContent, Radio, RadioGroup, FormControlLabel, TextField, Typography} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {submitSurvey} from "../../api/common";
import {setSnackbarOpen} from "../../store/taskSlice";
import {AppDispatch} from "../../store";
import {useMatomoTrackingPoint} from "../../hooks/useMatomoTrackingPoint";

interface SurveyDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmitted?: () => void;
    token?: string;
    boardId?: string;
    switchFrom?: string;
    uuid?: string;
}

interface SurveyErrors {
    q1?: string;
    q2?: string;
}

const Q1_OPTIONS = [
    {value: 'bilibili', label: 'B站'},
    {value: 'douyin', label: '抖音/抖音群'},
    {value: 'wechat_mp', label: '微信公众号'},
    {value: 'xiaohongshu', label: '小红书'},
    {value: 'search_engine', label: '搜索引擎（百度，谷歌等）'},
    {value: 'ai_search', label: 'AI搜索（豆包、千问等）'},
    {value: 'referral', label: '熟人推荐'},
    {value: 'community', label: '社群（Q群、微信群等）'},
    {value: 'other', label: '其它渠道'},
];

const Q2_OPTIONS = [
    {value: 'gaming', label: '游戏行业'},
    {value: 'media', label: '影视/短剧/漫剧'},
    {value: 'ecommerce', label: '电商零售'},
    {value: 'advertising', label: '广告营销'},
    {value: 'cultural', label: '文创潮玩'},
    {value: 'student', label: '在校学生'},
    {value: 'other', label: '其它行业'},
];

const BLUE = 'var(--c-accent)';
const LIGHT_GRAY = 'var(--c-bg-hover)';
const BORDER_GRAY = 'var(--c-border-light)';
const TEXT_DARK = 'var(--c-text-primary)';
const TEXT_GRAY = 'var(--c-text-weak)';
const TEXT_LIGHT = 'var(--c-text-muted)';

export default function SurveyDialog({open, onClose, onSubmitted, token, boardId, switchFrom, uuid}: SurveyDialogProps) {
    const dispatch: AppDispatch = useDispatch();
    const {trackQuestionaireSubmitClick} = useMatomoTrackingPoint();
    const [step, setStep] = useState(1);
    const [q1Value, setQ1Value] = useState<string>('');
    const [q1CommunityText, setQ1CommunityText] = useState<string>('');
    const [q1OtherText, setQ1OtherText] = useState<string>('');

    const [q2Value, setQ2Value] = useState<string>('');
    const [q2OtherText, setQ2OtherText] = useState<string>('');

    const [q3Value, setQ3Value] = useState<string>('');

    const [errors, setErrors] = useState<SurveyErrors>({});
    const [submitting, setSubmitting] = useState(false);

    const totalSteps = 3;

    const resetForm = () => {
        setStep(1);
        setQ1Value('');
        setQ1CommunityText('');
        setQ1OtherText('');
        setQ2Value('');
        setQ2OtherText('');
        setQ3Value('');
        setErrors({});
        setSubmitting(false);
    };

    useEffect(() => {
        if (open) resetForm();
    }, [open]);

    const validateStep = (): boolean => {
        const newErrors: SurveyErrors = {};
        if (step === 1) {
            if (!q2Value) {
                newErrors.q2 = '请选择一个选项';
            } else if (q2Value === 'other' && !q2OtherText.trim()) {
                newErrors.q2 = '请输入具体行业';
            }
        }
        if (step === 2) {
            if (!q1Value) {
                newErrors.q1 = '请选择一个选项';
            } else if (q1Value === 'other' && !q1OtherText.trim()) {
                newErrors.q1 = '请输入具体渠道';
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (!validateStep()) return;
        setStep(s => Math.min(s + 1, totalSteps));
    };

    const handleBack = () => {
        setErrors({});
        setStep(s => Math.max(s - 1, 1));
    };

    const handleSubmit = async () => {
        if (!validateStep()) return;

        setSubmitting(true);

        let q1Answer = Q1_OPTIONS.find(o => o.value === q1Value)?.label || q1Value;
        if (q1Value === 'community' && q1CommunityText.trim()) {
            q1Answer = `社群: ${q1CommunityText.trim()}`;
        } else if (q1Value === 'other' && q1OtherText.trim()) {
            q1Answer = `其它: ${q1OtherText.trim()}`;
        }

        let q2Answer = Q2_OPTIONS.find(o => o.value === q2Value)?.label || q2Value;
        if (q2Value === 'other' && q2OtherText.trim()) {
            q2Answer = `其它: ${q2OtherText.trim()}`;
        }

        try {
            const res = await submitSurvey({
                channel: q1Answer,
                industry: q2Answer,
                contact: q3Value.trim() || undefined,
            }, token);

            console.log(res, 'res')

            if (res?.code === 200) {
                setSubmitting(false);
                trackQuestionaireSubmitClick({
                    user_id: uuid,
                    page_id: window.location.href,
                    board_id: boardId || undefined,
                    switch_from: switchFrom || 'home',
                });
                onSubmitted?.();
                onClose();
            } else {
                dispatch(setSnackbarOpen({
                    open: true,
                    message: res?.message || '提交失败，请重试',
                }));
                setSubmitting(false);
            }
        } catch (e) {
            dispatch(setSnackbarOpen({
                open: true,
                message: '网络错误，请重试',
            }));
            setSubmitting(false);
        }
    };

    const handleDismiss = () => {
        onClose();
    };

    const progressPercent = (step / totalSteps) * 100;

    // ── Step 1: industry ──
    const renderStep1 = () => (
        <>
            <Typography sx={{
                fontSize: 22,
                fontWeight: 700,
                color: TEXT_DARK,
                textAlign: 'center',
                mb: 0.5,
                mt: 3,
            }}>
                请问您目前处于什么行业？
            </Typography>
            <Typography sx={{
                fontSize: 14,
                color: TEXT_GRAY,
                textAlign: 'center',
                mb: 4,
            }}>
                请选择您目前所处的行业，以便为您提供更精准的服务
            </Typography>
            <RadioGroup
                value={q2Value}
                onChange={(e) => {
                    setQ2Value(e.target.value);
                    if (errors.q2) setErrors(prev => ({...prev, q2: undefined}));
                }}
            >
                {Q2_OPTIONS.map(opt => (
                    <FormControlLabel
                        key={opt.value}
                        value={opt.value}
                        control={<Radio sx={{display: 'none'}}/>}
                        label={
                            <div style={{
                                width: '100%',
                                padding: '12px 20px',
                                border: `1px solid ${q2Value === opt.value ? BLUE : BORDER_GRAY}`,
                                borderRadius: 12,
                                backgroundColor: q2Value === opt.value ? 'var(--c-chip-bg)' : 'var(--c-bg)',
                                color: q2Value === opt.value ? BLUE : TEXT_DARK,
                                fontSize: 15,
                                fontWeight: q2Value === opt.value ? 500 : 400,
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                boxSizing: 'border-box',
                            }}
                                 onClick={() => {
                                     setQ2Value(opt.value);
                                     if (errors.q2) setErrors(prev => ({...prev, q2: undefined}));
                                 }}
                            >
                                {opt.label}
                            </div>
                        }
                        sx={{
                            width: '100%',
                            mx: 0,
                            mb: 1,
                            '& .MuiFormControlLabel-label': {width: '100%'},
                        }}
                    />
                ))}
            </RadioGroup>
            {q2Value === 'other' && (
                <TextField
                    placeholder="请输入具体行业"
                    variant="outlined"
                    size="small"
                    value={q2OtherText}
                    onChange={(e) => setQ2OtherText(e.target.value)}
                    sx={{
                        mt: 1,
                        mb: 2,
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            fontSize: 14,
                            backgroundColor: 'var(--c-bg-secondary)',
                            '& fieldset': {borderColor: BORDER_GRAY},
                            '&:hover fieldset': {borderColor: BLUE},
                            '&.Mui-focused fieldset': {borderColor: BLUE},
                        },
                    }}
                />
            )}
            {errors.q2 && (
                <Typography sx={{fontSize: 13, color: 'var(--c-danger)', mt: 0.5, textAlign: 'center'}}>
                    {errors.q2}
                </Typography>
            )}
        </>
    );

    // ── Step 2: channel ──
    const renderStep2 = () => (
        <>
            <Typography sx={{
                fontSize: 22,
                fontWeight: 700,
                color: TEXT_DARK,
                textAlign: 'center',
                mb: 0.5,
                mt: 3,
            }}>
                请问您从哪里知道Krene的？
            </Typography>
            <Typography sx={{
                fontSize: 14,
                color: TEXT_GRAY,
                textAlign: 'center',
                mb: 4,
            }}>
                请选择您从哪里知道Krene的，帮助我们了解您的来源
            </Typography>
            {Q1_OPTIONS.map(opt => (
                <div key={opt.value}>
                    <div
                        onClick={() => {
                            setQ1Value(opt.value);
                            if (errors.q1) setErrors(prev => ({...prev, q1: undefined}));
                        }}
                        style={{
                            width: '100%',
                            padding: '12px 20px',
                            border: `1px solid ${q1Value === opt.value ? BLUE : BORDER_GRAY}`,
                            borderRadius: 12,
                            backgroundColor: q1Value === opt.value ? 'var(--c-chip-bg)' : 'var(--c-bg)',
                            color: q1Value === opt.value ? BLUE : TEXT_DARK,
                            fontSize: 15,
                            fontWeight: q1Value === opt.value ? 500 : 400,
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            boxSizing: 'border-box',
                            marginBottom: q1Value === 'community' && opt.value === 'community' ? 0 : 8,
                        }}
                    >
                        {opt.label}
                    </div>
                    {q1Value === 'community' && opt.value === 'community' && (
                        <TextField
                            placeholder="请输入社群类型"
                            variant="outlined"
                            size="small"
                            value={q1CommunityText}
                            onChange={(e) => setQ1CommunityText(e.target.value)}
                            sx={{
                                mb: 1,
                                mt: 1,
                                width: '100%',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    fontSize: 14,
                                    backgroundColor: 'var(--c-bg-secondary)',
                                    '& fieldset': {borderColor: BORDER_GRAY},
                                    '&:hover fieldset': {borderColor: BLUE},
                                    '&.Mui-focused fieldset': {borderColor: BLUE},
                                },
                            }}
                        />
                    )}
                </div>
            ))}
            {q1Value === 'other' && (
                <TextField
                    placeholder="请输入具体渠道"
                    variant="outlined"
                    size="small"
                    value={q1OtherText}
                    onChange={(e) => setQ1OtherText(e.target.value)}
                    sx={{
                        mb: 2,
                        mt: 0.5,
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            fontSize: 14,
                            backgroundColor: 'var(--c-bg-secondary)',
                            '& fieldset': {borderColor: BORDER_GRAY},
                            '&:hover fieldset': {borderColor: BLUE},
                            '&.Mui-focused fieldset': {borderColor: BLUE},
                        },
                    }}
                />
            )}
            {errors.q1 && (
                <Typography sx={{fontSize: 13, color: 'var(--c-danger)', mt: 0.5, textAlign: 'center'}}>
                    {errors.q1}
                </Typography>
            )}
        </>
    );

    // ── Step 3: contact ──
    const renderStep3 = () => (
        <>
            <Typography sx={{
                fontSize: 22,
                fontWeight: 700,
                color: TEXT_DARK,
                textAlign: 'center',
                mb: 0.5,
                mt: 3,
            }}>
                您是否愿意留下联系方式？
            </Typography>
            <Typography sx={{
                fontSize: 14,
                color: TEXT_GRAY,
                textAlign: 'center',
                mb: 1.5,
                px: 2,
                lineHeight: '22px',
            }}>
                为不断优化产品和服务，我们诚邀您加入Krene产品共创计划，如您愿意参与，请在此处留下联系方式（微信/qq/邮箱均可），我们将不定期抽选用户进行回访。
            </Typography>
            <Typography sx={{
                fontSize: 13,
                color: TEXT_LIGHT,
                textAlign: 'center',
                mb: 3,
                px: 4,
                lineHeight: '20px',
            }}>
                参与回访的用户，将有机会获得 200-500 水滴奖励
            </Typography>
            <TextField
                placeholder="微信 / QQ / 邮箱（选填）"
                variant="outlined"
                size="small"
                fullWidth
                value={q3Value}
                onChange={(e) => setQ3Value(e.target.value)}
                sx={{
                    mb: 1,
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        fontSize: 14,
                        backgroundColor: 'var(--c-bg-secondary)',
                        '& fieldset': {borderColor: BORDER_GRAY},
                        '&:hover fieldset': {borderColor: BLUE},
                        '&.Mui-focused fieldset': {borderColor: BLUE},
                    },
                }}
            />
        </>
    );

    const isLastStep = step === totalSteps;
    const step1Ready = q2Value && !(q2Value === 'other' && !q2OtherText.trim());
    const step2Ready = q1Value && !(q1Value === 'other' && !q1OtherText.trim());
    const nextDisabled = (step === 1 && !step1Ready) || (step === 2 && !step2Ready);

    return (
        <Dialog
            open={open}
            aria-labelledby="survey-dialog-title"
            sx={{
                '& .MuiDialog-paper': {
                    maxWidth: 'none',
                    borderRadius: '16px',
                    width: 480,
                    boxShadow: 'none',
                    border: 'none',
                },
                '& .MuiDialogContent-root': {
                    padding: 0,
                },
                '& .MuiBackdrop-root': {
                    backgroundColor: 'var(--c-shadow-mid)',
                },
            }}
        >
            <DialogContent>
                <div style={{
                    padding: '24px 32px 20px',
                    backgroundColor: 'var(--c-bg)',
                    borderRadius: 16,
                }}>
                    {/* ── Top bar: back + page ── */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 16,
                    }}>
                        {step > 1 ? (
                            <div
                                onClick={handleBack}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    color: BLUE,
                                    fontSize: 14,
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                }}
                            >
                                <ArrowBackIcon sx={{fontSize: 18}}/>
                                后退
                            </div>
                        ) : (
                            <div/>
                        )}
                        <div style={{
                            fontSize: 13,
                            color: TEXT_LIGHT,
                            fontWeight: 400,
                            letterSpacing: '0.3px',
                        }}>
                            {step}/{totalSteps}
                        </div>
                    </div>

                    {/* ── Progress bar ── */}
                    <div style={{
                        width: '100%',
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: LIGHT_GRAY,
                        overflow: 'hidden',
                    }}>
                        <div style={{
                            width: `${progressPercent}%`,
                            height: '100%',
                            borderRadius: 2,
                            backgroundColor: BLUE,
                            transition: 'width 0.3s ease',
                        }}/>
                    </div>

                    {/* ── Step content ── */}
                    <div style={{minHeight: 340}}>
                        {step === 1 && renderStep1()}
                        {step === 2 && renderStep2()}
                        {step === 3 && renderStep3()}
                    </div>

                    {/* ── Bottom actions ── */}
                    <div style={{
                        display: 'flex',
                        justifyContent: step === 1 ? 'flex-end' : 'space-between',
                        alignItems: 'center',
                        marginTop: 16,
                        paddingTop: 16,
                        borderTop: `1px solid ${LIGHT_GRAY}`,
                    }}>
                        {step > 1 && (
                            <div></div>
                        )}
                        {step < 3 && (
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                disabled={nextDisabled}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: 14,
                                    borderRadius: '20px',
                                    px: 4,
                                    py: 0.8,
                                    backgroundColor: nextDisabled ? LIGHT_GRAY : BLUE,
                                    color: nextDisabled ? TEXT_LIGHT : 'var(--c-on-color)',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: nextDisabled ? LIGHT_GRAY : 'var(--c-accent)',
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                下一步 →
                            </Button>
                        )}
                        {step === 3 && (
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={submitting}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: 14,
                                    borderRadius: '20px',
                                    px: 4,
                                    py: 0.8,
                                    backgroundColor: BLUE,
                                    color: 'var(--c-on-color)',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: 'var(--c-accent)',
                                        boxShadow: 'none',
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: LIGHT_GRAY,
                                        color: TEXT_LIGHT,
                                    },
                                }}
                            >
                                {submitting ? '提交中…' : '完成'}
                            </Button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
